import { useState } from 'react';
import { useTranslation } from 'react-i18next';

import { Button } from '@/components/common/Button';
import { EsitoLoginFinestra } from '@/components/common/EsitoLoginFinestra';
import { useLogtoAuth } from '@/hooks/useLogtoAuth';
import { clearBridgeDebug, getBridgeDebug } from '@/lib/logto';

/**
 * Pre-auth landing screen. Shown by AppShell when the user has no Logto
 * session yet. The Sign in button uses `useLogtoAuth().signIn`, which routes
 * through the Telegram-aware bridge: inside the Telegram WebView it opens
 * Logto in the system browser via `openLink` (Google blocks OAuth in embedded
 * WebViews), then deep-links back via `t.me/<bot>/app?startapp=auth_<code>`.
 * In a standalone browser it uses Logto SDK's redirect flow.
 */
export function Login() {
  const { signIn, isLoading } = useLogtoAuth();
  const { t } = useTranslation('general');
  const [showDebug, setShowDebug] = useState(false);
  const debugLog = showDebug ? getBridgeDebug() : null;

  return (
    <div className="flex min-h-screen flex-col items-center justify-center gap-8 bg-bg p-6 text-text">
      <div className="flex flex-col items-center gap-3">
        <h1 className="text-4xl font-bold tracking-tight text-brand">QLASH</h1>
        <p className="text-center text-sm text-fg-muted">
          {t('auth.tagline')}
        </p>
      </div>

      <button
        type="button"
        disabled={isLoading}
        onClick={() => signIn()}
        className="rounded-md bg-brand px-8 py-3 text-base font-medium text-brand-fg transition-opacity active:opacity-80 disabled:opacity-50"
      >
        {isLoading ? t('shell.loading') : t('auth.signIn')}
      </button>
      {import.meta.env.VITE_FEATURE_LOGIN_FINESTRA_ENABLED === 'true' ? <EsitoLoginFinestra /> : null}

      <Button
        variant="link"
        onClick={() => setShowDebug((v) => !v)}
        className="font-normal text-fg-muted underline"
      >
        {showDebug ? t('auth.hideDebug') : t('auth.showDebug')}
      </Button>
      {showDebug && (
        <div className="w-full max-w-md">
          <pre className="max-h-[40vh] overflow-auto whitespace-pre-wrap break-words rounded-md border border-border bg-bg-secondary p-3 text-[10px] leading-tight text-fg-muted">
            {debugLog || t('auth.noBridgeEvents')}
          </pre>
          <Button
            variant="link"
            onClick={() => {
              clearBridgeDebug();
              setShowDebug(false);
              setTimeout(() => setShowDebug(true), 0);
            }}
            className="mt-2 font-normal text-fg-muted underline"
          >
            {t('auth.clearLog')}
          </Button>
        </div>
      )}
    </div>
  );
}
